import { collapseWithMap, expandWordBounds, findQuoteSpans, rawSpan } from "./libraryHighlightText";

const BLOCK_SELECTOR = [
  "p",
  "div",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "li",
  "blockquote",
  "section",
  "article",
  "header",
  "footer",
  "aside",
  "pre",
  "figcaption",
  "td",
  "th",
  "dd",
  "dt",
  "caption",
  "tr",
  "address",
].join(", ");

function blockOf(node) {
  const parent = node.parentElement;
  if (!parent) return null;
  return parent.closest(BLOCK_SELECTOR) || parent;
}

function containsBr(element) {
  if (!element || element.nodeType !== 1) return false;
  if (element.tagName === "BR") return true;
  return Boolean(element.querySelector?.("br"));
}

function breakBetween(previous, node) {
  if (!previous) return false;
  if (blockOf(previous) !== blockOf(node)) return true;
  if (previous.parentNode === node.parentNode) {
    let sibling = previous.nextSibling;
    while (sibling && sibling !== node) {
      if (containsBr(sibling)) return true;
      sibling = sibling.nextSibling;
    }
    return false;
  }
  const range = previous.ownerDocument.createRange();
  try {
    range.setStartAfter(previous);
    range.setEndBefore(node);
  } catch {
    return false;
  }
  if (range.collapsed) return false;
  return Boolean(range.cloneContents().querySelector("br"));
}

function flatText(root) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      if (node.parentElement?.closest(".library-mark__remove")) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  let raw = "";
  const parts = [];
  let previous = null;
  while (walker.nextNode()) {
    const node = walker.currentNode;
    if (breakBetween(previous, node)) raw += "\n";
    previous = node;
    const start = raw.length;
    raw += node.nodeValue;
    parts.push({ node, start, end: raw.length });
  }
  return { raw, parts };
}

function unwrapMarks(root) {
  root.querySelectorAll(".library-mark").forEach((mark) => {
    const parent = mark.parentNode;
    if (!parent) return;
    while (mark.firstChild) {
      if (mark.firstChild.classList?.contains("library-mark__remove")) {
        mark.removeChild(mark.firstChild);
        continue;
      }
      parent.insertBefore(mark.firstChild, mark);
    }
    parent.removeChild(mark);
  });
  root.normalize();
}

function flatRoots(roots) {
  if (roots.length === 1) return flatText(roots[0]);
  return flatTextPair(roots[0], roots[1]);
}

export function applyHighlights(root, highlights) {
  applyHighlightsToPages(root ? [root] : [], highlights);
}

function markElement(highlight, text, withButton) {
  const mark = document.createElement("mark");
  const tone = highlight.kind === "word" ? "word" : highlight.kind === "favorite" ? "favorite" : "passage";
  mark.className = `library-mark library-mark--${tone}`;
  mark.dataset.highlightId = highlight.id;
  mark.append(text);
  if (withButton) {
    mark.dataset.end = "1";
    const button = document.createElement("button");
    button.type = "button";
    button.className = "library-mark__remove";
    button.dataset.highlightId = highlight.id;
    button.setAttribute("aria-label", "Retirer le surlignage");
    mark.append(button);
  }
  return mark;
}

function wrapJobs(parts, jobs) {
  const kept = [];
  jobs.forEach((job) => {
    if (kept.some((other) => job.start < other.end && other.start < job.end)) return;
    kept.push(job);
  });
  const grouped = new Map();
  parts.forEach((part) => {
    const cuts = [];
    kept.forEach((job) => {
      if (job.end <= part.start || job.start >= part.end) return;
      cuts.push({
        start: Math.max(0, job.start - part.start),
        end: Math.min(part.node.nodeValue.length, job.end - part.start),
        highlight: job.highlight,
        withButton: job.end <= part.end,
      });
    });
    if (cuts.length) grouped.set(part, cuts);
  });
  grouped.forEach((cuts, part) => {
    if (!part.node.parentNode) return;
    cuts.sort((a, b) => a.start - b.start || b.end - a.end);
    const value = part.node.nodeValue || "";
    const fragment = document.createDocumentFragment();
    let cursor = 0;
    cuts.forEach((cut) => {
      if (cut.end <= cut.start || cut.start < cursor) return;
      if (cut.start > cursor) fragment.append(value.slice(cursor, cut.start));
      fragment.append(markElement(cut.highlight, value.slice(cut.start, cut.end), cut.withButton));
      cursor = cut.end;
    });
    if (cursor < value.length) fragment.append(value.slice(cursor));
    part.node.parentNode.replaceChild(fragment, part.node);
  });
}

/** Les deux pages d'une double page forment un seul texte, pour un passage coupé au milieu. */
export function applyHighlightsToPages(pages, highlights) {
  const roots = (Array.isArray(pages) ? pages : []).filter(Boolean).slice(0, 2);
  if (!roots.length) return;
  roots.forEach(unwrapMarks);
  const list = (Array.isArray(highlights) ? highlights : [])
    .filter((highlight) => highlight?.quote && highlight.id)
    .sort((a, b) => b.quote.length - a.quote.length);
  if (!list.length) return;
  const { raw, parts } = flatRoots(roots);
  const collapsed = collapseWithMap(raw);
  const spans = findQuoteSpans(collapsed.text, list);
  const jobs = [];
  list.forEach((highlight, index) => {
    const span = spans[index];
    if (!span) return;
    const rawRange = rawSpan(collapsed.toRaw, raw.length, span.start, span.end);
    if (!rawRange) return;
    jobs.push({ start: rawRange.start, end: rawRange.end, highlight });
  });
  wrapJobs(parts, jobs);
}

export function selectionTouchesMark(range) {
  if (!range) return false;
  const from = (node) => (node?.nodeType === 1 ? node : node?.parentElement)?.closest?.(".library-mark");
  if (from(range.startContainer) || from(range.endContainer)) return true;
  const ancestor = range.commonAncestorContainer;
  const element = ancestor?.nodeType === 1 ? ancestor : ancestor?.parentElement;
  if (!element?.querySelectorAll) return false;
  for (const mark of element.querySelectorAll(".library-mark")) {
    try {
      if (range.intersectsNode(mark)) return true;
    } catch {
      /* le nœud n'est plus dans le document */
    }
  }
  return false;
}

function pageElement(node) {
  const element = node.nodeType === 1 ? node : node.parentElement;
  return element?.closest?.(".library-spread__page, .library-spread__flow") || null;
}

function flatTextPair(first, second) {
  const start = flatText(first);
  const end = flatText(second);
  const gap = start.raw && end.raw ? "\n" : "";
  const shift = start.raw.length + gap.length;
  return {
    raw: start.raw + gap + end.raw,
    parts: start.parts.concat(
      end.parts.map((part) => ({
        node: part.node,
        start: part.start + shift,
        end: part.end + shift,
      }))
    ),
  };
}

export function readSelection(root) {
  const selection = window.getSelection();
  if (!selection || selection.isCollapsed || selection.rangeCount !== 1) return null;
  const range = selection.getRangeAt(0);
  if (!root.contains(range.startContainer) || !root.contains(range.endContainer)) return null;
  const startPage = pageElement(range.startContainer);
  const endPage = pageElement(range.endContainer);
  if (!startPage || !endPage) return null;
  const { raw, parts } = startPage === endPage ? flatText(startPage) : flatTextPair(startPage, endPage);
  const collapsed = collapseWithMap(raw);
  const startPart = parts.find((part) => part.node === range.startContainer);
  const endPart = parts.find((part) => part.node === range.endContainer);
  if (!startPart || !endPart) return null;
  const rawStart = startPart.start + range.startOffset;
  const rawEnd = endPart.start + range.endOffset;
  if (rawEnd <= rawStart) return null;
  let collapsedStart = 0;
  while (
    collapsedStart < collapsed.toRaw.length &&
    collapsed.toRaw[collapsedStart] < rawStart
  ) {
    collapsedStart += 1;
  }
  let collapsedEnd = collapsedStart;
  while (
    collapsedEnd < collapsed.toRaw.length &&
    collapsed.toRaw[collapsedEnd] < rawEnd
  ) {
    collapsedEnd += 1;
  }
  const bounds = expandWordBounds(collapsed.text, collapsedStart, collapsedEnd);
  const quoteStart = bounds.start;
  const quoteEnd = bounds.end;
  const quote = collapsed.text.slice(quoteStart, quoteEnd);
  if (!quote) return null;
  const before = collapsed.text.slice(Math.max(0, quoteStart - 80), quoteStart);
  const after = collapsed.text.slice(quoteEnd, quoteEnd + 80);
  return { quote, before, after, start: quoteStart, end: quoteEnd, text: collapsed.text };
}
